import { motion } from 'framer-motion';
import { BookOpen, Briefcase, Building, Building2, CheckCircle, Compass, Dumbbell, GraduationCap, HardHat, Heart, Home, Key, Landmark, Laptop, ShieldCheck, ShoppingBag, Smartphone, Star, Ticket, Truck } from 'lucide-react';
import { useState } from 'react';
import QRCode from 'react-qr-code';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import SocialShare from '../components/SocialShare';
import { AppStoreBadge, GooglePlayBadge } from '../components/StoreBadges';
import { trackCTA, trackDemoView, trackFormSubmit } from '../utils/analytics';

const HS_PORTAL_ID = process.env.REACT_APP_HS_PORTAL_ID;
const HS_FORM_GUID = process.env.REACT_APP_HS_FORM_GUID;

const submitPilotToHubSpot = async ({ name, email, phone, sector }) => {
  if (!HS_PORTAL_ID || !HS_FORM_GUID) return;
  const [firstname, ...rest] = name.trim().split(' ');
  await fetch(
    `https://api.hsforms.com/submissions/v3/integration/submit/${HS_PORTAL_ID}/${HS_FORM_GUID}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fields: [
          { name: 'firstname', value: firstname },
          { name: 'lastname', value: rest.join(' ') || '' },
          { name: 'email', value: email },
          { name: 'phone', value: phone || '' },
          { name: 'message', value: `Demo pilot request — sector: ${sector}` },
        ],
        context: { pageUri: window.location.href, pageName: document.title },
      }),
    }
  );
};

const fallbackUrl = process.env.REACT_APP_DEMO_QR_URL || '';

const VERTICALS = [
  {
    id: 'hotel',
    label: 'Hotels & Resorts',
    Icon: Building2,
    tagline: 'Guest check-in & registration',
    path: '/hotels',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_HOTEL || fallbackUrl,
  },
  {
    id: 'health',
    label: 'Clinics & Hospitals',
    Icon: Heart,
    tagline: 'Patient onboarding & verification',
    path: '/healthcare',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_HEALTH || fallbackUrl,
  },
  {
    id: 'corporate',
    label: 'Corporate Offices',
    Icon: Briefcase,
    tagline: 'Visitor & employee check-in',
    path: '/corporate',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_CORPORATE || fallbackUrl,
  },
  {
    id: 'events',
    label: 'Events & Venues',
    Icon: Ticket,
    tagline: 'Attendee registration',
    path: '/events',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_EVENTS || fallbackUrl,
  },
  {
    id: 'bank',
    label: 'Banks & NBFCs',
    Icon: Landmark,
    tagline: 'Branch KYC & account opening',
    path: '/banking',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_BANK || fallbackUrl,
  },
  {
    id: 'gated',
    label: 'Gated Communities',
    Icon: Home,
    tagline: 'Resident & visitor gate check-in',
    path: '/gated-communities',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_GATED || fallbackUrl,
  },
  {
    id: 'govt',
    label: 'Govt / Jan Seva Kendra',
    Icon: Building,
    tagline: 'Citizen service centre access',
    path: '/jan-seva-kendra',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_GOVT || fallbackUrl,
  },
  {
    id: 'gym',
    label: 'Gym & Fitness Centres',
    Icon: Dumbbell,
    tagline: 'Member onboarding & daily check-in',
    path: '/gyms',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_GYM || fallbackUrl,
  },
  {
    id: 'coworking',
    label: 'Co-working Spaces',
    Icon: Laptop,
    tagline: 'Member & visitor access log',
    path: '/co-working',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_COWORKING || fallbackUrl,
  },
  {
    id: 'coaching',
    label: 'Coaching Institutes',
    Icon: BookOpen,
    tagline: 'Student enrolment verification',
    path: '/coaching',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_COACHING || fallbackUrl,
  },
  {
    id: 'schools',
    label: 'Schools & Colleges',
    Icon: GraduationCap,
    tagline: 'Campus visitor & exam check-in',
    path: '/schools',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_SCHOOLS || fallbackUrl,
  },
  {
    id: 'hostels',
    label: 'Hostels & PGs',
    Icon: Key,
    tagline: 'Tenant verification & check-in',
    path: '/hostels',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_HOSTELS || fallbackUrl,
  },
  {
    id: 'retail',
    label: 'Retail & Malls',
    Icon: ShoppingBag,
    tagline: 'Staff, contractor & VIP entry',
    path: '/retail',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_RETAIL || fallbackUrl,
  },
  {
    id: 'religious',
    label: 'Temples & Trusts',
    Icon: Star,
    tagline: 'Devotee registration & events',
    path: '/religious-trusts',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_RELIGIOUS || fallbackUrl,
  },
  {
    id: 'logistics',
    label: 'Logistics & Warehouses',
    Icon: Truck,
    tagline: 'Gate pass & contractor check-in',
    path: '/logistics',
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_LOGISTICS || fallbackUrl,
  },
  {
    id: 'age-gated-retail',
    label: 'Age Gated Retail',
    Icon: ShieldCheck,
    tagline: 'Age verification for tobacco, alcohol & adult stores',
    path: null,
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_AGE_GATED_RETAIL || fallbackUrl,
  },
  {
    id: 'pilgrim',
    label: 'Pilgrim',
    Icon: Compass,
    tagline: 'VIP Dharshan Check-in',
    path: null,
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_RELIGIOUS || fallbackUrl,
  },
  {
    id: 'construction',
    label: 'Construction Sites',
    Icon: HardHat,
    tagline: 'Worker attendance & site check-in',
    path: null,
    qrUrl: process.env.REACT_APP_DEMO_QR_URL_CONSTRUCTION || fallbackUrl,
  },
];

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20';

const Demo = () => {
  const [selectedId, setSelectedId] = useState('hotel');
  const vertical = VERTICALS.find(v => v.id === selectedId);
  const qrUrl = vertical.qrUrl;

  const [pilotLoading, setPilotLoading] = useState(false);
  const [pilotSubmitted, setPilotSubmitted] = useState(false);
  const [pilotError, setPilotError] = useState('');

  const handlePilotSubmit = async e => {
    e.preventDefault();
    setPilotError('');
    setPilotLoading(true);
    const form = e.currentTarget;
    try {
      await submitPilotToHubSpot({
        name: form.pilotName.value,
        email: form.pilotEmail.value,
        phone: form.pilotPhone.value,
        sector: vertical.label,
      });
      trackFormSubmit('demo_pilot_request', { sector: selectedId });
      setPilotSubmitted(true);
    } catch {
      setPilotError('Something went wrong. Email us at sales@idgate.in');
    } finally {
      setPilotLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Live Demo — Aadhaar Check-in"
        description="Try IDGate live. Scan the QR code with your mAadhaar or DigiLocker app and experience a full Aadhaar-verified hotel check-in in under 30 seconds — no paper, no manual ID check."
        keywords="Aadhaar check-in demo, IDGate demo, live Aadhaar verification, mAadhaar QR scan demo, digital hotel check-in demo India"
        url="https://idgate.in/demo"
      />

      {/* Hero band */}
      <div className="border-b border-slate-200 bg-white">
        <div className="container-custom py-14 lg:py-20">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-accent-600">
            Live demo
          </p>
          <h1 className="max-w-3xl font-black tracking-tight">
            <span className="block text-3xl font-semibold text-slate-900 lg:text-4xl">
              See Aadhaar check-in
            </span>
            <span className="demo-hero-gradient block text-6xl leading-tight lg:text-8xl">
              in action.
            </span>
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-600">
            Scan the QR code with your Camera mobile app or QR code scanner app. The flow takes under 30 seconds — no account needed.
          </p>
        </div>
      </div>

      {/* Vertical picker */}
      <div className="border-b border-slate-100 bg-white">
        <div className="container-custom py-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Choose your sector
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {VERTICALS.map(({ id, label, Icon, tagline }) => {
              const active = id === selectedId;
              return (
                <button
                  key={id}
                  onClick={() => { setSelectedId(id); trackDemoView(id); }}
                  className={`flex w-full items-center gap-2.5 rounded-xl border px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                    active
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={18} className={active ? 'text-primary-600' : 'text-slate-400'} />
                  <div>
                    <p className={`text-sm font-semibold leading-tight ${active ? 'text-primary-800' : 'text-slate-800'}`}>
                      {label}
                    </p>
                    <p className={`text-xs leading-tight ${active ? 'text-primary-600' : 'text-slate-400'}`}>
                      {tagline}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          {vertical.path && (
            <p className="mt-3 text-xs text-slate-400">
              Learn how IDGate works for{' '}
              <Link
                to={vertical.path}
                className="font-medium text-primary-600 underline underline-offset-2 hover:text-primary-800"
                onClick={() => trackCTA(`learn_more_${vertical.id}`, 'demo_picker')}
              >
                {vertical.label} &#8250;
              </Link>
            </p>
          )}
        </div>
      </div>

      {/* Two-panel section */}
      <div className="bg-surface-page">
        <div className="container-custom py-14 lg:py-20">
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-20">

            {/* Left panel */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="order-2 lg:order-1"
            >
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">
                What this demo shows
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                IDGate turns any check-in into an Aadhaar-verified moment. When someone scans this
                code, the Aadhaar app presents exactly which identity claims are being requested
                and asks for their explicit consent — verified by India&apos;s official identity
                infrastructure in real time. No paper. No photocopies. No manual entry. A single
                verified record, delivered instantly to your system.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                This is a live flow from our sandbox — the exact same technology running in
                hotels, clinics, offices, events, banks, and regulated businesses across India.
              </p>


              {/* Aadhaar app prerequisite */}
              <div className="mt-8 rounded-2xl border border-accent-200 bg-accent-50 p-5">
                <div className="flex items-start gap-3">
                  <Smartphone className="mt-0.5 shrink-0 text-accent-600" size={20} />
                  <div>
                    <p className="text-sm font-semibold text-accent-900">
                      Don&apos;t have the Aadhaar app yet?
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-accent-700">
                      The QR scan opens the official <strong>Aadhaar app</strong> to complete
                      identity verification. Download it once before scanning — it only takes a
                      minute.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <GooglePlayBadge href="https://play.google.com/store/search?q=aadhaar+app&c=apps&hl=en_IN" />
                      <AppStoreBadge href="https://apps.apple.com/in/app/aadhaar/id6744029871" />
                    </div>
                    <p className="mt-3 border-t border-accent-200 pt-3 text-xs leading-relaxed text-accent-600">
                      <strong>This is a one-time install.</strong> Once set up, you&apos;ll never need
                      to carry a plastic Aadhaar card, hand over a photocopy, or fill a manual
                      register again — just scan the QR at any IDGate-enabled check-in point and
                      you&apos;re done. Your Aadhaar stays on your phone, under your control.
                    </p>
                  </div>
                </div>
              </div>

              {/* How to scan */}
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-soft">
                <p className="mb-5 text-sm font-semibold text-slate-800">How to scan</p>
                <ol className="space-y-5">

                  {/* Step 0 — prerequisite */}
                  <li className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-widest text-amber-700">
                      Before you begin
                    </p>
                    <p className="mt-1 text-sm text-slate-700">
                      Make sure the <strong>Aadhaar app</strong> is installed — the QR launches it
                      to complete verification. Download it from the{' '}
                      <a href="https://play.google.com/store/search?q=aadhaar+app&c=apps&hl=en_IN" target="_blank" rel="noopener noreferrer" className="font-medium text-accent-600 underline underline-offset-2">Google Play Store</a>
                      {' '}or the{' '}
                      <a href="https://apps.apple.com/in/app/aadhaar/id6744029871" target="_blank" rel="noopener noreferrer" className="font-medium text-accent-600 underline underline-offset-2">App Store</a>
                      {' '}if you haven&apos;t already.
                    </p>
                    <p className="mt-2 text-xs text-slate-400">Already installed? Jump straight to step 1.</p>
                  </li>

                  {/* Step 1 */}
                  <li className="flex gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                      1
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        Point your camera at the QR code
                      </p>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                        Use your phone&apos;s camera app or any QR scanner. No login or account needed — just aim and tap the link.
                      </p>
                    </div>
                  </li>

                  {/* Step 2 */}
                  <li className="flex gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                      2
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        Aadhaar app opens — review what&apos;s being shared
                      </p>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                        You&apos;ll see exactly which identity claims are requested. Nothing is shared without your explicit tap — you&apos;re in full control.
                      </p>
                    </div>
                  </li>

                  {/* Step 3 */}
                  <li className="flex gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                      3
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        Tap <span className="text-primary-700">Allow</span> — verified in seconds
                      </p>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                        UIDAI confirms your identity and a verified record reaches the organisation instantly. Check-in complete.
                      </p>
                    </div>
                  </li>

                </ol>
              </div>
            </motion.div>

            {/* Right panel — QR + pilot form */}
            <div className="order-1 flex flex-col items-center lg:order-2 lg:sticky lg:top-24">
              <p className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <vertical.Icon size={16} className="text-primary-600" />
                {vertical.label} demo
              </p>

              <div className="inline-block rounded-2xl border border-slate-200 bg-white p-6 shadow-soft">
                {qrUrl ? (
                  <QRCode value={qrUrl} size={220} />
                ) : (
                  <div className="flex h-[220px] w-[220px] items-center justify-center rounded-xl bg-slate-100 text-sm text-slate-400">
                    Demo link not configured
                  </div>
                )}
              </div>

              <p className="mt-5 text-sm font-semibold text-slate-700">
                Scan with your Camera or QR Code Scanner app
              </p>
              <p className="mt-1 text-xs text-slate-400">QR refreshes automatically if expired</p>

              {qrUrl && (
                <a
                  href={qrUrl}
                  rel="noopener noreferrer"
                  className="mt-6 flex items-center gap-2 rounded-lg border border-accent-500 px-5 py-2.5 text-sm font-semibold text-accent-600 transition-colors hover:bg-accent-50"
                  onClick={() => trackCTA('open_demo_device', `demo_${selectedId}`)}
                >
                  Open on this device instead
                </a>
              )}

              <p className="mt-6 max-w-xs text-center text-xs leading-relaxed text-slate-400">
                Your Aadhaar data is shared directly with IDGate&apos;s sandbox. Nothing is stored
                beyond the demo session.
              </p>

              {/* Pilot form */}
              <div className="mt-8 w-full rounded-2xl border border-primary-100 bg-primary-50 p-5">
                {pilotSubmitted ? (
                  <div className="text-center">
                    <CheckCircle className="mx-auto h-8 w-8 text-green-500" aria-hidden />
                    <p className="mt-3 text-sm font-semibold text-slate-900">You&apos;re on the list.</p>
                    <p className="mt-1 text-xs text-slate-500">
                      We&apos;ll reach out within one business day.
                    </p>
                    <Link
                      to="/contact"
                      className="mt-3 inline-block text-xs font-medium text-primary-600 underline underline-offset-2"
                    >
                      Tell us more about your setup ›
                    </Link>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-slate-900">
                      Want this at your {vertical.label.toLowerCase()}?
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Free 30-day pilot. No contract, no card.
                    </p>
                    <form onSubmit={handlePilotSubmit} className="mt-4 space-y-3">
                      <input name="pilotName" type="text" required placeholder="Your name" className={inputClass} />
                      <input name="pilotEmail" type="email" required placeholder="Work email" className={inputClass} />
                      <input name="pilotPhone" type="tel" placeholder="Phone (optional)" className={inputClass} />
                      {pilotError && <p className="text-xs text-red-600">{pilotError}</p>}
                      <button
                        type="submit"
                        disabled={pilotLoading}
                        className="btn-primary w-full disabled:opacity-60"
                        onClick={() => trackCTA(`pilot_submit_${selectedId}`, 'demo_pilot_form')}
                      >
                        {pilotLoading ? 'Sending…' : 'Get free pilot'}
                      </button>
                    </form>
                  </>
                )}
              </div>

              <SocialShare
                url="https://idgate.in/demo"
                text="Try IDGate's live Aadhaar check-in demo — verified identity in under 30 seconds, no paper, no manual ID checks."
                className="mt-6"
              />
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Demo;
